import { ChevronDown, FlaskConical, Loader2, Play, Wrench } from 'lucide-react';
import { Link } from 'react-router-dom';

import { OutcomeBadge } from '@/components/calls/OutcomeBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatDateTime } from '@/utils/format';

import { CALL_DIRECTION_BADGES, CALL_STATUS_BADGES } from '@repo/shared-types';

import type { CallWithContact, WorkflowEntity } from '@repo/shared-types';

interface CallDetailHeaderProps {
  call: CallWithContact;
  availableWorkflows: WorkflowEntity[];
  workflowRunning: boolean;
  selectedWorkflowId: string;
  onWorkflowIdChange: (id: string) => void;
  onRunWorkflow: (dryRun: boolean) => void;
  onReplay?: () => void;
  onFixAndVerify?: () => void;
  fixAndVerifyPending?: boolean;
  // Read-only viewers (clients view) can't run workflows
  readOnly?: boolean;
}

export const CallDetailHeader = ({
  call,
  availableWorkflows,
  workflowRunning,
  selectedWorkflowId,
  onWorkflowIdChange,
  onRunWorkflow,
  onReplay,
  onFixAndVerify,
  fixAndVerifyPending = false,
  readOnly = false,
}: CallDetailHeaderProps) => {
  const durationMinutes = Math.floor(call.duration / 60);
  const durationSeconds = call.duration % 60;
  const durationLabel =
    durationMinutes > 0 ? `${durationMinutes}m ${durationSeconds}s` : `${durationSeconds}s`;

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">Call detail</p>
          <h1 className="text-2xl font-bold tracking-tight break-all sm:text-3xl">{call.id}</h1>
          <p className="text-muted-foreground">Executed {formatDateTime(call.executed_at)}</p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          {!readOnly && availableWorkflows.length > 0 && (
            <select
              value={selectedWorkflowId}
              onChange={(e) => onWorkflowIdChange(e.target.value)}
              className="border-input bg-background ring-offset-background focus-visible:ring-ring rounded-md border px-3 py-1.5 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
            >
              <option value="">Auto (configuration default)</option>
              {availableWorkflows.map((wf) => (
                <option key={wf.id} value={wf.id}>
                  {wf.name}
                </option>
              ))}
            </select>
          )}
          {onFixAndVerify && !call.is_test && (
            <div className="flex items-center">
              <Button
                size="sm"
                className="w-full sm:w-auto"
                disabled={fixAndVerifyPending}
                onClick={onFixAndVerify}
              >
                <Wrench size={14} className="mr-1" />
                {fixAndVerifyPending ? 'Starting...' : 'Fix & verify'}
              </Button>
              {onReplay && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" className="ml-1 h-8 w-8">
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={onReplay}>
                      <FlaskConical size={14} className="mr-2" />
                      Replay as test
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          )}
          {!onFixAndVerify && onReplay && !call.is_test && (
            <Button variant="outline" size="sm" className="w-full sm:w-auto" onClick={onReplay}>
              <FlaskConical size={14} className="mr-1" /> Replay as test
            </Button>
          )}
          {!readOnly && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto"
                  disabled={workflowRunning}
                >
                  {workflowRunning ? (
                    <>
                      <Loader2 size={14} className="mr-1 animate-spin" /> Running...
                    </>
                  ) : (
                    <>
                      <Play size={14} className="mr-1" /> Run Workflow
                    </>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onRunWorkflow(true)}>
                  Dry Run (no emails/SMS)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onRunWorkflow(false)}>Live Run</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button variant="outline" asChild className="w-full sm:w-auto">
            <Link to="/calls">Back to calls</Link>
          </Button>
        </div>
      </div>

      {/* Status bar with inline badges */}
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className={CALL_DIRECTION_BADGES[call.direction]}>
          {call.direction}
        </Badge>
        {call.call_transport && <Badge variant="outline">{call.call_transport}</Badge>}
        <Badge variant="outline" className={CALL_STATUS_BADGES[call.status]}>
          {call.status}
        </Badge>
        <OutcomeBadge outcome={call.outcome} />
        <Badge variant="secondary">{durationLabel}</Badge>
        {call.configuration_version ? (
          <Badge variant="outline">v{call.configuration_version.version_number}</Badge>
        ) : (
          <Badge variant="destructive">Version Deleted</Badge>
        )}
        {call.is_test && (
          <Badge variant="secondary" className="text-amber-700 dark:text-amber-400">
            <FlaskConical className="mr-1 h-3 w-3" />
            Test call
          </Badge>
        )}
      </div>
    </div>
  );
};
